// Ayarlar → Profil → İki aşamalı doğrulama (TOTP). Backend: /auth/2fa/* (app/twofa.py, routers/auth_router.py)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { useLang, addTranslations } from './i18n';

addTranslations({
  'İki aşamalı doğrulama': 'Two-factor authentication',
  'Giriş yaparken şifrenin yanında authenticator uygulamasından (Google Authenticator, Microsoft Authenticator, 1Password vb.) 6 haneli bir kod istenir.': 'When you sign in, a 6-digit code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, etc.) is requested in addition to your password.',
  'Açık': 'On', 'Kapalı': 'Off',
  'Kurulumu başlat': 'Start setup',
  '1. Authenticator uygulamasında QR kodu tarayın (ya da aşağıdaki anahtarı elle girin).': '1. Scan the QR code in your authenticator app (or enter the key below manually).',
  'Kurulum anahtarı': 'Setup key',
  'Telefonda mısınız? Uygulamada aç': 'On your phone? Open in app',
  '2. Uygulamada görünen 6 haneli kodu girin.': '2. Enter the 6-digit code shown in the app.',
  'Doğrula ve aç': 'Verify and turn on',
  'Vazgeç': 'Cancel',
  'Yedek kodlarınızı kaydedin': 'Save your backup codes',
  'Telefonunuza erişemezseniz her kod bir kez kullanılabilir. Bu kodlar bir daha gösterilmez; güvenli bir yere kaydedin.': 'If you lose access to your phone, each code can be used once. They will not be shown again; store them somewhere safe.',
  'Kopyala': 'Copy', 'Kopyalandı': 'Copied', 'Kaydettim': 'I saved them',
  'kalan yedek kod': 'backup codes left',
  'Kapat': 'Turn off',
  'Kapatmak için şifrenizi ve güncel kodu (ya da bir yedek kodu) girin.': 'To turn it off, enter your password and a current code (or a backup code).',
  'Şifre': 'Password', 'Doğrulama kodu': 'Verification code',
  'İki aşamalı doğrulamayı kapat': 'Turn off two-factor authentication',
  'Bir hata oluştu, tekrar dener misin?': 'Something went wrong, please try again.',
});

const label: React.CSSProperties = { fontSize: 9, fontWeight: 700, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif', marginBottom: 6 };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${T.borderMid}`, fontSize: 14, fontFamily: 'Manrope, sans-serif', background: T.subtleBg, outline: 'none', color: T.text, boxSizing: 'border-box' };
const gold: React.CSSProperties = { padding: '9px 16px', borderRadius: 11, border: 'none', background: T.accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' };
const ghost: React.CSSProperties = { padding: '9px 16px', borderRadius: 11, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.text, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' };

export default function TwoFactorCard() {
  const { t } = useLang();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [left, setLeft] = useState(0);
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string; qr_svg: string } | null>(null);
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [offOpen, setOffOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => axios.get('/api/v1/auth/2fa/status').then(r => { setEnabled(!!r.data.enabled); setLeft(r.data.backup_codes_left || 0); }).catch(() => setEnabled(false));
  useEffect(() => { load(); }, []);

  const fail = (e: any) => setErr(e?.response?.data?.detail || t('Bir hata oluştu, tekrar dener misin?'));

  const start = async () => {
    setBusy(true); setErr('');
    try { const r = await axios.post('/api/v1/auth/2fa/setup'); setSetup(r.data); setCode(''); } catch (e) { fail(e); }
    setBusy(false);
  };
  const enable = async () => {
    setBusy(true); setErr('');
    try { const r = await axios.post('/api/v1/auth/2fa/enable', { code }); setBackup(r.data.backup_codes || []); setSetup(null); setCode(''); await load(); } catch (e) { fail(e); }
    setBusy(false);
  };
  const disable = async () => {
    setBusy(true); setErr('');
    try { await axios.post('/api/v1/auth/2fa/disable', { password: pw, code }); setOffOpen(false); setPw(''); setCode(''); await load(); } catch (e) { fail(e); }
    setBusy(false);
  };
  const copy = () => {
    try { navigator.clipboard.writeText((backup || []).join('\n')); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* sessiz */ }
  };

  return (
    <div style={{ background: T.surface, borderRadius: 16, border: `1px solid ${T.border}`, padding: '22px 24px' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: T.accent, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif', marginBottom: 4 }}>{t('Güvenlik')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.text, fontFamily: 'Manrope, sans-serif' }}>{t('İki aşamalı doğrulama')}</div>
        {enabled !== null && (
          <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', background: enabled ? T.greenTint : T.trackBg, color: enabled ? T.greenText : T.textMidAlt }}>
            {enabled ? t('Açık') : t('Kapalı')}
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: T.textMidAlt, lineHeight: 1.6, marginBottom: 16, maxWidth: 640 }}>
        {t('Giriş yaparken şifrenin yanında authenticator uygulamasından (Google Authenticator, Microsoft Authenticator, 1Password vb.) 6 haneli bir kod istenir.')}
      </div>

      {err && <div style={{ fontSize: 12, color: T.redText, padding: '10px 14px', background: T.redTint, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      {backup && (
        <div style={{ background: T.subtleBg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, maxWidth: 520 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>{t('Yedek kodlarınızı kaydedin')}</div>
          <div style={{ fontSize: 12.5, color: T.textMidAlt, lineHeight: 1.55, marginBottom: 12 }}>{t('Telefonunuza erişemezseniz her kod bir kez kullanılabilir. Bu kodlar bir daha gösterilmez; güvenli bir yere kaydedin.')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
            {backup.map(c => <code key={c} style={{ fontFamily: MONO, fontSize: 14, padding: '8px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, textAlign: 'center', letterSpacing: '0.05em' }}>{c}</code>)}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={copy} style={ghost}>{copied ? t('Kopyalandı') : t('Kopyala')}</button>
            <button onClick={() => setBackup(null)} style={gold}>{t('Kaydettim')}</button>
          </div>
        </div>
      )}

      {!backup && enabled === false && !setup && (
        <button onClick={start} disabled={busy} style={gold}>{t('Kurulumu başlat')}</button>
      )}

      {!backup && setup && (
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>{t('1. Authenticator uygulamasında QR kodu tarayın (ya da aşağıdaki anahtarı elle girin).')}</div>
          <div style={{ display: 'inline-block', background: '#fff', padding: 8, borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 12, maxWidth: '100%' }}
            dangerouslySetInnerHTML={{ __html: setup.qr_svg }} />
          <div style={label}>{t('Kurulum anahtarı')}</div>
          <code style={{ display: 'block', fontFamily: MONO, fontSize: 14, wordBreak: 'break-all', padding: '10px 12px', background: T.subtleBg, border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 8, color: T.text }}>{setup.secret}</code>
          <a href={setup.otpauth_uri} style={{ fontSize: 12.5, color: T.accentDeep, fontWeight: 600 }}>{t('Telefonda mısınız? Uygulamada aç')}</a>
          <div style={{ fontSize: 13, color: T.text, margin: '18px 0 8px' }}>{t('2. Uygulamada görünen 6 haneli kodu girin.')}</div>
          <input value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength={8}
            onKeyDown={e => { if (e.key === 'Enter' && code.trim()) enable(); }} style={{ ...input, maxWidth: 220, marginBottom: 12, letterSpacing: '0.2em', fontFamily: MONO }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={enable} disabled={busy || !code.trim()} style={{ ...gold, opacity: code.trim() ? 1 : 0.5 }}>{t('Doğrula ve aç')}</button>
            <button onClick={() => { setSetup(null); setErr(''); }} style={ghost}>{t('Vazgeç')}</button>
          </div>
        </div>
      )}

      {!backup && enabled === true && (
        <div>
          <div style={{ fontSize: 12.5, color: T.textMidAlt, marginBottom: 12 }}>{left} {t('kalan yedek kod')}</div>
          {!offOpen ? (
            <button onClick={() => { setOffOpen(true); setErr(''); setCode(''); }} style={ghost}>{t('Kapat')}</button>
          ) : (
            <div style={{ maxWidth: 420 }}>
              <div style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>{t('Kapatmak için şifrenizi ve güncel kodu (ya da bir yedek kodu) girin.')}</div>
              <div style={label}>{t('Şifre')}</div>
              <input type="password" autoComplete="current-password" value={pw} onChange={e => setPw(e.target.value)} style={{ ...input, marginBottom: 12 }} />
              <div style={label}>{t('Doğrulama kodu')}</div>
              <input value={code} onChange={e => setCode(e.target.value)} autoComplete="one-time-code" style={{ ...input, marginBottom: 14, fontFamily: MONO }} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={disable} disabled={busy || !pw || !code.trim()} style={{ ...gold, background: T.redText, opacity: pw && code.trim() ? 1 : 0.5 }}>{t('İki aşamalı doğrulamayı kapat')}</button>
                <button onClick={() => { setOffOpen(false); setErr(''); }} style={ghost}>{t('Vazgeç')}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
